import { IField } from '@hooks/useField';
import useLoading from '@hooks/useLoading';
import { IconButton, InputAdornment, TextField, TextFieldProps } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { IEndereco } from '@model/Endereco';
import useCepService from '@services/CepService';
import React from 'react';
import InputMask from 'react-input-mask';
import { InputAdornmentProgress } from '../InputAdornmentProgress';

export type CepFieldProps = TextFieldProps & {
    field: IField
    updateFieldStateOnChange?: boolean
    onValueChanged?: (newValue: any) => void
    onEnter?: React.KeyboardEventHandler<HTMLDivElement> | undefined

    onCepSearch: (endereco: Partial<IEndereco>) => void
}

function CepFieldComponent ({
    onCepSearch, 
    disabled, 
    onChange, 
    field, 
    onValueChanged, 
    onEnter,
    onKeyDown,
    updateFieldStateOnChange, 
    fullWidth,
    helperText,
    ...props
}: CepFieldProps) {
    const [_value, _setValue] = React.useState(field.value);

    const loading = useLoading();
    const cepService = useCepService();

    const isCepComplete = React.useCallback(() => {
        const value = (_value || "").toString().length;
        return value === 10;
    }, [_value]);

    const handleCepSearch = React.useCallback(() => {
        if (!loading.isLoading && onCepSearch !== undefined && isCepComplete()) {
            loading.start();
            cepService.get(_value)
                .then(response => {
                    if (response !== null) {
                        onCepSearch(response);
                    }
                })
                .catch(() => {})
                .finally(loading.stop);
        }
    }, [_value, cepService, isCepComplete, loading, onCepSearch]);

    const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value;
        
        if (v !== _value) {
            _setValue(v);            
        }

    }, [_value]);

    const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" && onEnter !== undefined) {
            onEnter(e);
        }

        if (onKeyDown !== undefined) {
            onKeyDown(e);
        }
    }, [onEnter, onKeyDown]);

    //Reage à mudança do valor interno
    //Set o valor na ref
    //Chama o evento o change externo
    //Verifica se é para subir o estado automaticamente através da propriedade updateFieldStateOnChange
    React.useEffect(() => {
        field.setInputValue(_value);

        if (onValueChanged !== undefined) {
            onValueChanged(_value);
        }

        if (updateFieldStateOnChange === true && field.value !== _value) {
            field.setValue(_value);
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_value, onValueChanged, updateFieldStateOnChange]);

    const setFormattedValue = React.useCallback((value: any) => {
        if (value === undefined || value === null || value === "") {
            _setValue("");
        }
        else {
            const cep = value.toString().replace(/\D/g, "");
            _setValue(cep);
        }
    }, []);
    
    //Reage à mudança do valor externo
    //Atualiza o valor interno pelo valor externo
    React.useEffect(() => {
        if (field.value !== _value) {
            _setValue(field.value);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.value, setFormattedValue]);

    React.useEffect(() => {
        if (field.transition === "reset") {
            _setValue(field.initialValueRef?.current);
            field.clearTransition();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.transition]);

    return (
        <InputMask
            mask="99.999-999"
            maskChar={null}
            alwaysShowMask={false}
            readOnly={props.InputProps?.readOnly}
            value={_value || ""}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={disabled}
        >
            {() => 
                <TextField
                    {...props}
                    disabled={disabled}
                    name={field.name}
                    value={_value !== undefined && _value !== null ? _value : ""}
                    fullWidth={fullWidth !== false}
                    helperText={!!field.error ? field.error : helperText}
                    error={!!field.error}
                    InputProps={{
                        ...props.InputProps,
                        endAdornment: (
                            <InputAdornment position="end">
                            {
                                loading.isLoading
                                ? (
                                    <InputAdornmentProgress />
                                )
                                : onCepSearch !== undefined && isCepComplete() && (
                                    <IconButton
                                        onClick={handleCepSearch}
                                    >
                                        <SearchIcon />
                                    </IconButton>
                                )
                            }
                            </InputAdornment>
                        )               
                    }}
                />
        }
        </InputMask>
    );
}

export const CepField = React.memo(CepFieldComponent);